import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useToast } from '@/hooks/useToast';
import DarkPageContainer from '@/shared/components/DarkPageContainer';
import { Card } from '@/shared/components/Card';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '@/shared/components/Button';
import { DarkPageHeader, DarkSectionHeader, DarkAlert, DarkBadge, DarkCheckbox, DarkInput, DarkEmptyState } from '@/shared/components/DarkFormElements';
import SpendingSummary from '@/modules/user/components/SpendingSummary';
import { useApps, useBuildQueue, useApp } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function DashboardPage() {
  const { authFetch, user } = useAuthContext();
  const navigate = useNavigate();
  const { showToast, toasts, hideToast } = useToast();
  
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMonthlyFeeWithoutPaymentMethod, setHasMonthlyFeeWithoutPaymentMethod] = useState(false);
  const [totalMonthlyFee, setTotalMonthlyFee] = useState(0);
  const [paymentCheckAppId, setPaymentCheckAppId] = useState(null);

  // Query hooks
  const { data: apps = [], isLoading: appsLoading, error: appsError } = useApps();
  const { data: queueRequests = [], isLoading: queueLoading } = useBuildQueue();
  
  // Find first live app with fees to check payment method
  const liveAppsWithFees = apps.filter(app => 
    app.status === 'live' && 
    parseFloat(app.monthlyMaintenanceFee || '0') > 0
  );
  const firstAppWithFees = liveAppsWithFees[0];
  
  // Conditionally fetch app detail for payment method check
  const { data: appDetail } = useApp(firstAppWithFees?.id);

  // Check payment method status when app detail loads
  useEffect(() => {
    if (appDetail && liveAppsWithFees.length > 0) {
      if (!appDetail.hasMonthlyPaymentMethod) {
        setHasMonthlyFeeWithoutPaymentMethod(true);
        const total = liveAppsWithFees.reduce((sum, app) => 
          sum + parseFloat(app.monthlyMaintenanceFee || '0'), 0
        );
        setTotalMonthlyFee(total);
      } else {
        setHasMonthlyFeeWithoutPaymentMethod(false);
      }
    }
  }, [appDetail, liveAppsWithFees.length]);

  const loading = appsLoading || queueLoading;

  // Show error toast if apps fail to load
  useEffect(() => {
    if (appsError) {
      showToast('Failed to load dashboard data', 'error');
    }
  }, [appsError]);

  // Queue returns apps with work status 'draft' or 'in_progress'
  const appsInQueue = queueRequests.filter(app => app.status === 'draft' || app.status === 'in_progress');

  // Filter apps
  let filteredApps = showArchived 
    ? apps 
    : apps.filter(app => app.status !== 'archived');
  
  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredApps = filteredApps.filter(app => {
      const matchesName = app.name?.toLowerCase().includes(query);
      const matchesDescription = app.description?.toLowerCase().includes(query);
      const matchesTags = app.tags && app.tags.some(tag => tag.toLowerCase().includes(query));
      return matchesName || matchesDescription || matchesTags;
    });
  }

  // Sort apps by creation date (newest first)
  filteredApps = [...filteredApps].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateB - dateA;
  });

  return (
    <DarkPageContainer
      toasts={toasts}
      onCloseToast={hideToast}
      loading={loading}
      loadingText="Loading dashboard..."
    >
      {/* Header */}
      <DarkPageHeader
        title="Dashboard"
        actions={
          <PrimaryButton onClick={() => navigate('/app-summary')} size="lg">
            + Create New App
          </PrimaryButton>
        }
      />

      {/* Payment Method Notice */}
      {hasMonthlyFeeWithoutPaymentMethod && (
        <DarkAlert variant="warning" icon="💳" title="Payment Method Required for Monthly Fees" className="mb-6">
          <p className="mb-3">
            You have live apps with monthly maintenance fees totaling <strong>${totalMonthlyFee.toFixed(2)}/month</strong>, 
            but no payment method is configured.
          </p>
          <p className="text-xs opacity-75 mb-4">
            Note: Subscriptions are per-user (covering all your apps), not per-app.
          </p>
          <button
            onClick={async () => {
              try {
                const returnUrl = `${window.location.origin}/dashboard`;
                const response = await authFetch(`/api/users/customer-portal?returnUrl=${encodeURIComponent(returnUrl)}`);
                
                if (!response.ok) {
                  const error = await response.json();
                  throw new Error(error.error || 'Failed to open billing portal');
                }
                
                const data = await response.json();
                window.location.href = data.portalUrl;
              } catch (error) {
                captureError(error, {
                  action: 'handleOpenBillingPortal',
                  component: 'DashboardPage',
                });
                showToast(error.message || 'Failed to open billing portal', 'error');
              }
            }}
            className="px-5 py-2.5 bg-yellow-500 text-yellow-900 rounded-lg hover:bg-yellow-400 font-semibold transition-colors"
          >
            Set Up Payment Method →
          </button>
        </DarkAlert>
      )}

      {/* Queue Section */}
      {appsInQueue.length > 0 && (
        <div className="mb-8">
          <DarkSectionHeader title="Build Queue" subtitle={`${appsInQueue.length} app${appsInQueue.length !== 1 ? 's' : ''} in progress`} />
          <Card variant="dark" padding="none">
            {appsInQueue.map((app, index) => (
              <div
                key={app.appId}
                className={`p-5 sm:p-6 hover:bg-white/5 transition-colors ${
                  index !== appsInQueue.length - 1 ? 'border-b border-white/10' : ''
                }`}
              >
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                  <div className="flex-1 w-full">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-white break-words">{app.appName}</h3>
                      {app.isNext && (
                        <DarkBadge variant="yellow">Next Up</DarkBadge>
                      )}
                      {app.status === 'in_progress' && (
                        <DarkBadge variant="info">In Progress</DarkBadge>
                      )}
                    </div>
                    {app.appDescription && (
                      <p className="text-gray-400 mb-2 text-sm line-clamp-2">{app.appDescription}</p>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-500">
                      {app.queuePosition && (
                        <span>
                          Position: <span className="font-semibold text-gray-300">#{app.queuePosition}</span> of {app.totalInQueue}
                        </span>
                      )}
                      {app.aheadCount !== null && app.aheadCount > 0 && (
                        <span>{app.aheadCount} app{app.aheadCount !== 1 ? 's' : ''} ahead</span>
                      )}
                    </div>
                  </div>
                  <SecondaryButton
                    onClick={() => navigate(`/app/${app.appId}`)}
                    size="sm"
                    variant="outline"
                    className="w-full sm:w-auto min-h-[44px]"
                  >
                    View App
                  </SecondaryButton>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Spending Summary */}
      <div className="mb-8">
        <SpendingSummary showToast={showToast} dark={true} />
      </div>

      {/* Apps Section */}
      <div className="mb-8">
        <DarkSectionHeader 
          title="Your Apps"
          action={
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <DarkCheckbox
                label="Show archived"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            </div>
          }
        />

        {/* Search */}
        <div className="mb-6">
          <DarkInput
            type="text"
            placeholder="Search apps by name, description, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {filteredApps.length === 0 ? (
          <Card variant="dark" className="text-center py-12">
            <DarkEmptyState
              icon="📱"
              title={
                searchQuery.trim()
                  ? 'No apps match your search'
                  : showArchived 
                    ? "You don't have any apps yet"
                    : apps.length === 0
                      ? "You don't have any apps yet"
                      : 'No active apps'
              }
              description={
                searchQuery.trim()
                  ? 'Try a different search term'
                  : apps.length === 0
                    ? 'Create your first app to get started!'
                    : 'Toggle "Show archived" to see archived apps'
              }
              action={
                apps.length === 0 && !searchQuery.trim() && (
                  <PrimaryButton onClick={() => navigate('/app-summary')} size="lg">
                    Create Your First App
                  </PrimaryButton>
                )
              }
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredApps.map((app) => (
              <Card
                key={app.id}
                variant="dark"
                onClick={() => navigate(`/app/${app.id}`)}
                className="group"
              >
                <h3 className="text-lg font-bold text-white mb-2 break-words group-hover:text-zapt-yellow transition-colors">
                  {app.name}
                </h3>
                {app.description && (
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">{app.description}</p>
                )}
                {app.tags && app.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {app.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 bg-zapt-purple/30 text-purple-200 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                    {app.tags.length > 3 && (
                      <span className="inline-flex items-center px-2 py-0.5 bg-white/10 text-gray-400 rounded text-xs">
                        +{app.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <DarkBadge variant={
                      app.status === 'live' ? 'success' :
                      app.status === 'draft' ? 'default' :
                      app.status === 'archived' ? 'error' :
                      'default'
                    }>
                      {app.status}
                    </DarkBadge>
                    {app.createdAt && (
                      <span className="text-xs text-gray-500">
                        {new Date(app.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    )}
                  </div>
                  {app.appUrl && app.status !== 'archived' && (
                    <a
                      href={app.appUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm text-zapt-yellow hover:text-zapt-yellow/80 font-medium"
                    >
                      Open →
                    </a>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DarkPageContainer>
  );
}
